import { Box, MenuItem, Select, type SxProps, Tooltip, Typography } from '@mui/material'

import langs from '@/assets/allLangs.json'
import { type CardActivityWithId } from '@/models/card-activity/card-activity.model'

export default function FeedbackLanguageSelector({
  value,
  onChange,
  label = 'Feedback Language',
  targetLanguage = 'en',
  sx,
  selectSx,
}: {
  value?: CardActivityWithId['feedback_language']
  onChange?: (value: CardActivityWithId['feedback_language']) => void
  label?: string
  targetLanguage?: string
  sx?: SxProps
  selectSx?: SxProps
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        ...sx,
      }}
    >
      {label ? (
        <TitleText
          link="https://www.notion.so/speakable/A-Guide-to-Speakable-s-Activity-Builder-acc92d9f56624fcfb96ba486fa10b427?pvs=4#007635e5cc1b478e8ce59463cade97a0"
          tooltipText="Select the language for the AI feedback."
        >
          {label}
        </TitleText>
      ) : null}

      <Select
        size="small"
        sx={{
          width: '150px',
          mb: '8px',
          borderRadius: '16px',
          '& .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #D0D5DD !important',
            borderRadius: '16px !important',
          },
          '& .MuiInputBase-input': {
            padding: '14px',
            color: '#344054',
            fontWeight: 500,
          },
          '&.MuiInputBase-root': {
            height: 44,
            background: '#FFF',
            maxWidth: '290px',
          },
          ...selectSx,
        }}
        value={value}
        onChange={e => {
          onChange?.(e.target.value as CardActivityWithId['feedback_language'])
        }}
      >
        <MenuItem value="target">{langs[targetLanguage as keyof typeof langs]}</MenuItem>
        <MenuItem value="native">Native Language</MenuItem>
      </Select>
    </Box>
  )
}

export function TitleText({
  children,
  tooltipText,
  subtitle,
  link,
}: {
  children: React.ReactNode
  tooltipText: string
  subtitle?: string
  link?: string
}) {
  const handleLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!link) return
    e.stopPropagation()
    window.open(link, '_blank')
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '0px',
        flexDirection: 'column',
      }}
    >
      <Tooltip arrow placement="top" title={tooltipText}>
        <Typography
          fontSize={15}
          fontWeight={500}
          mb={1}
          sx={{
            '&:hover': {
              cursor: link && 'pointer',
              textDecoration: link && 'underline',
            },
          }}
          onClick={handleLinkClick}
        >
          {children}
        </Typography>
      </Tooltip>
      {subtitle ? (
        <Typography color="text.secondary" fontSize={12} fontWeight={400}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  )
}
